import React, { useState, useMemo, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import DashboardLayout from "./Layout/DashboardLayout";
import { createStore } from "redux";
import AlertReducer from "./Redux/AlertReducer";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline"; // Reset CSS for MUI
import LoginPage from "./Page/login";
import { ToastContainer } from "react-toastify";
import { ProtectedRoute } from "./routes/ProtectedRoute";
import { AuthProvider } from "./Providers/AuthContext";
import Unauthorized from "./Page/unauthorized";
import NotFound from "./Page/default/notfound";
import { isLoggedIn } from "./utils/auth";

const store = createStore(AlertReducer);
function App() {
  const [themeMode, setThemeMode] = useState(
    localStorage.getItem("theme") === "light"
      ? "light"
      : localStorage.getItem("theme") === "dark"
      ? "dark"
      : "light"
  ); // State to manage theme mode

  // Define light and dark themes
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeMode,
          primary: {
            main: "#114787",
            contrastText: "#fff",
          },
          secondary: {
            main: "#f0f3f7",
          },
        },
      }),
    [themeMode]
  );

  useEffect(() => {
    const darkModeEnabled = localStorage.getItem("theme") === "dark";
    if (darkModeEnabled) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [themeMode]);

  // Toggle theme handler
  const toggleTheme = () => {
    setThemeMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
    localStorage.setItem("theme", themeMode === "light" ? "dark" : "light");
  };

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ToastContainer />
        <Router>
          <AuthProvider>
            <Routes>
              <Route
                path="/login"
                element={<LoginPage thememode={themeMode} />}
              />
              <Route path="/unauthorized" element={<Unauthorized />} />

              {/* Protected Routes - Authentication and Permission Required */}
              <Route
                path="/Dashboard"
                element={
                  <ProtectedRoute requiredPermission="dashboard.stats">
                    <DashboardLayout
                      content="Dashboard"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Reviews"
                element={
                  <ProtectedRoute requiredPermission="panelsReviews.index">
                    <DashboardLayout
                      content="Reviews"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Support"
                element={
                  <ProtectedRoute requiredPermission="supportTickets.index">
                    <DashboardLayout
                      content="Support"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Support/:id"
                element={
                  <ProtectedRoute requiredPermission="supportTickets.messages">
                    <DashboardLayout
                      content="Support-Chat"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/TelegramGroups"
                element={
                  <ProtectedRoute requiredPermission="telegramGroups.index">
                    <DashboardLayout
                      content="TelegramGroups"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/AddTelegramGroups"
                element={
                  <ProtectedRoute requiredPermission="telegramGroups.store">
                    <DashboardLayout
                      content="AddTelegramGroups"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="TelegramGroups/Edit/:id"
                element={
                  <ProtectedRoute requiredPermission="telegramGroups.update">
                    <DashboardLayout
                      content="EditTelegramGroups"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/skypeGroups"
                element={
                  <ProtectedRoute requiredPermission="skypeGroups.index">
                    <DashboardLayout
                      content="SkypeGroups"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/AddSkypeGroups"
                element={
                  <ProtectedRoute requiredPermission="skypeGroups.store">
                    <DashboardLayout
                      content="AddSkypeGroups"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="SkypeGroups/Edit/:id"
                element={
                  <ProtectedRoute requiredPermission="skypeGroups.update">
                    <DashboardLayout
                      content="EditSkypeGroups"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/WhatsappGroups"
                element={
                  <ProtectedRoute requiredPermission="whatsappGroups.index">
                    <DashboardLayout
                      content="WhatsappGroups"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/AddWhatsappGroups"
                element={
                  <ProtectedRoute requiredPermission="whatsappGroups.store">
                    <DashboardLayout
                      content="AddWhatsappGroups"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="WhatsappGroups/Edit/:id"
                element={
                  <ProtectedRoute requiredPermission="whatsappGroups.update">
                    <DashboardLayout
                      content="EditWhatsappGroups"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Shop-ADS"
                element={
                  <ProtectedRoute requiredPermission="ourServices.index">
                    <DashboardLayout
                      content="Shop-ADS"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Add-AD"
                element={
                  <ProtectedRoute requiredPermission="ourServices.store">
                    <DashboardLayout
                      content="Add-AD"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Edit-AD/:id"
                element={
                  <ProtectedRoute requiredPermission="ourServices.update">
                    <DashboardLayout
                      content="Edit-AD"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Offers"
                element={
                  <ProtectedRoute requiredPermission="offers.index">
                    <DashboardLayout
                      content="Offers"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Add-Offer"
                element={
                  <ProtectedRoute requiredPermission="offers.store">
                    <DashboardLayout
                      content="Add-Offer"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Offers/Edit/:id"
                element={
                  <ProtectedRoute requiredPermission="offers.update">
                    <DashboardLayout
                      content="Edit-Offer"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Ads-Pin"
                element={
                  <ProtectedRoute requiredPermission="pins.index">
                    <DashboardLayout
                      content="Ads-Pin"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Ads-Pin/Edit/:id"
                element={
                  <ProtectedRoute requiredPermission="pins.update">
                    <DashboardLayout
                      content="Edit-Ads-Pin"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Ads-Main-Page"
                element={
                  <ProtectedRoute requiredPermission="banners.index">
                    <DashboardLayout
                      content="Ads-Main-Page"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Ads-Main-Page/Edit/:id"
                element={
                  <ProtectedRoute requiredPermission="banners.update">
                    <DashboardLayout
                      content="Edit-Ads-Main-Page"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Ads-Search-Result"
                element={
                  <ProtectedRoute requiredPermission="topResults.index">
                    <DashboardLayout
                      content="Ads-Search-Result"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Ads-Ticket"
                element={
                  <ProtectedRoute requiredPermission="ticketAds.index">
                    <DashboardLayout
                      content="Ads-Ticket"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Ads-Ticket/Edit/:id"
                element={
                  <ProtectedRoute requiredPermission="ticketAds.update">
                    <DashboardLayout
                      content="Edit-Ads-Ticket"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Orders"
                element={
                  <ProtectedRoute requiredPermission="orders.index">
                    <DashboardLayout
                      content="Orders"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Add-Order-By-Service"
                element={
                  <ProtectedRoute requiredPermission="orders.store">
                    <DashboardLayout
                      content="Add-Order-By-Service"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Add-Order-By-Offer"
                element={
                  <ProtectedRoute requiredPermission="offerOrders.store">
                    <DashboardLayout
                      content="Add-Order-By-Offer"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Orders/:id"
                element={
                  <ProtectedRoute requiredPermission="dashboard.stats">
                    <DashboardLayout
                      content="Order-Details"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route path="/Users">
                <Route
                  index
                  element={
                    <ProtectedRoute requiredPermission="users.index">
                      <DashboardLayout
                        content="Users"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Add"
                  element={
                    <ProtectedRoute requiredPermission="users.store">
                      <DashboardLayout
                        content="Add-Users"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <ProtectedRoute requiredPermission="users.show">
                      <DashboardLayout
                        content="User-Details"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="edit/:id"
                  element={
                    <ProtectedRoute requiredPermission="users.update">
                      <DashboardLayout
                        content="User-Edit"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="/Employees">
                <Route
                  index
                  element={
                    <ProtectedRoute requiredPermission="employees.index">
                      <DashboardLayout
                        content="Employees"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Add"
                  element={
                    <ProtectedRoute requiredPermission="employees.store">
                      <DashboardLayout
                        content="Add-Employee"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Edit/:id"
                  element={
                    <ProtectedRoute requiredPermission="employees.update">
                      <DashboardLayout
                        content="Edit-Employee"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="/Panels">
                <Route
                  index
                  element={
                    <ProtectedRoute requiredPermission="panels.index">
                      <DashboardLayout
                        content="Panels"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Add"
                  element={
                    <ProtectedRoute requiredPermission="panels.store">
                      <DashboardLayout
                        content="Add-Panel"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Edit/:id"
                  element={
                    <ProtectedRoute requiredPermission="panels.update">
                      <DashboardLayout
                        content="Edit-Panel"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <ProtectedRoute requiredPermission="panels.show">
                      <DashboardLayout
                        content="View-Panel"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Trusted"
                  element={
                    <ProtectedRoute requiredPermission="panels.index">
                      <DashboardLayout
                        content="View-Trusted-Panel"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Log"
                  element={
                    // <ProtectedRoute requiredPermission="panels.log'">
                    <DashboardLayout
                      content="View-Panels-Log"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                    // </ProtectedRoute>
                  }
                />
                <Route
                  path="UpdatedPanels"
                  element={
                    // <ProtectedRoute requiredPermission="panels.log'">
                    <DashboardLayout
                      content="View-UpdatedPanels"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                    // </ProtectedRoute>
                  }
                />
                <Route
                  path="PanelsByType"
                  element={
                    // <ProtectedRoute requiredPermission="panels.log'">
                    <DashboardLayout
                      content="View-PanelsByType"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                    // </ProtectedRoute>
                  }
                />
              </Route>

              <Route
                path=""
                element={
                  <ProtectedRoute requiredPermission="dashboard.stats">
                    <DashboardLayout
                      content="Dashboard"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />

              <Route path="/SubCategories">
                <Route
                  index
                  element={
                    <ProtectedRoute requiredPermission="subcategories.index">
                      <DashboardLayout
                        content="SubCategories"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Add"
                  element={
                    <ProtectedRoute requiredPermission="subcategories.store">
                      <DashboardLayout
                        content="Add-SubCategory"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Edit/:id"
                  element={
                    <ProtectedRoute requiredPermission="subcategories.update">
                      <DashboardLayout
                        content="Edit-SubCategory"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <ProtectedRoute requiredPermission="subcategories.show">
                      <DashboardLayout
                        content="View-SubCategory"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
              </Route>

              <Route path="/Categories">
                <Route
                  index
                  element={
                    <ProtectedRoute requiredPermission="categories.index">
                      <DashboardLayout
                        content="Categories"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Add"
                  element={
                    <ProtectedRoute requiredPermission="categories.store">
                      <DashboardLayout
                        content="Add-Category"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Edit/:id"
                  element={
                    <ProtectedRoute requiredPermission="categories.update">
                      <DashboardLayout
                        content="Edit-Category"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path=":id"
                  element={
                    <ProtectedRoute requiredPermission="categories.show">
                      <DashboardLayout
                        content="View-Category"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
              </Route>

              <Route path="*" element={<NotFound />} />

              <Route path="/SubCategories">
                <Route
                  index
                  element={
                    <ProtectedRoute requiredPermission="subcategories.index">
                      <DashboardLayout
                        content="SubCategories"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Add"
                  element={
                    <ProtectedRoute requiredPermission="subcategories.store">
                      <DashboardLayout
                        content="Add-SubCategory"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Edit/:id"
                  element={
                    <ProtectedRoute requiredPermission="subcategories.update">
                      <DashboardLayout
                        content="Edit-SubCategory"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <ProtectedRoute requiredPermission="subcategories.show">
                      <DashboardLayout
                        content="View-SubCategory"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
              </Route>

              <Route path="/Categories">
                <Route
                  index
                  element={
                    <ProtectedRoute requiredPermission="categories.index">
                      <DashboardLayout
                        content="Categories"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Add"
                  element={
                    <ProtectedRoute requiredPermission="categories.store">
                      <DashboardLayout
                        content="Add-Categories"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Edit/:id"
                  element={
                    <ProtectedRoute requiredPermission="categories.update">
                      <DashboardLayout
                        content="Edit-Categories"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <ProtectedRoute requiredPermission="categories.show">
                      <DashboardLayout
                        content="View-Categories"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="/Faqs">
                <Route
                  index
                  element={
                    <ProtectedRoute requiredPermission="faqs.index">
                      <DashboardLayout
                        content="Faqs"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Add"
                  element={
                    <ProtectedRoute requiredPermission="faqs.store">
                      <DashboardLayout
                        content="Add-Faqs"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Edit/:id"
                  element={
                    <ProtectedRoute requiredPermission="faqs.update">
                      <DashboardLayout
                        content="Edit-Faqs"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <ProtectedRoute requiredPermission="faqs.show">
                      <DashboardLayout
                        content="View-Faqs"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
              </Route>

              <Route path="/Marketers">
                <Route
                  index
                  element={
                    <ProtectedRoute requiredPermission="marketers.index">
                      <DashboardLayout
                        content="Marketers"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Add"
                  element={
                    <ProtectedRoute requiredPermission="marketers.store">
                      <DashboardLayout
                        content="Add-Marketers"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Edit/:id"
                  element={
                    <ProtectedRoute requiredPermission="marketers.update">
                      <DashboardLayout
                        content="Edit-Marketers"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <ProtectedRoute requiredPermission="marketers.show">
                      <DashboardLayout
                        content="View-Marketers"
                        toggleTheme={toggleTheme}
                        thememode={themeMode}
                      />
                    </ProtectedRoute>
                  }
                />
              </Route>

              <Route path="/Settings">
                <Route
                  index
                  element={
                    <DashboardLayout
                      content="Settings"
                      toggleTheme={toggleTheme}
                      thememode={themeMode}
                    />
                  }
                />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </div>
  );
}

export default App;
